import { DisplayRightsComposer } from '@/components/display-rights/DisplayRightsComposer';
import ModalShell from '@/components/ui/modal-shell';
import { useCompanionPanelStore } from '@/stores/companion-panel-store';
import { Calendar } from 'iconoir-react';
import { useTranslation } from 'react-i18next';

/**
 * Where a renewal card's call to action actually opens.
 *
 * The composer is the one the brand already uses from the contact card, not a
 * copy of it: the same server-resolved gesture, the same conditions, the same
 * send. All this host does is put one authorization in front of it and offer
 * the conditions the card named as a starting point.
 *
 * It lives in the shell rather than in the panel because the panel closes as
 * soon as the composer opens, and a modal owned by a component that unmounts
 * would close with it.
 *
 * Nothing is sent from here. The composer's own button is still the only thing
 * that reaches the creator, and the card only closes once it has.
 */
export const CompanionDisplayRightsCtaHost = () => {
  const { t } = useTranslation();
  const request = useCompanionPanelStore((state) => state.displayRightsCta);
  const closeDisplayRightsCta = useCompanionPanelStore((state) => state.closeDisplayRightsCta);

  if (!request) return null;

  return (
    <ModalShell
      // A new card gets a new composer, so nothing typed for one authorization
      // survives into another's.
      key={request.actionId}
      isOpen
      onClose={closeDisplayRightsCta}
      maxWidth="520px"
      headerIcon={<Calendar width={16} height={16} />}
      step={{
        title: t('companion.cta.displayRights.title'),
        subtitle: request.creatorName ?? undefined,
      }}
    >
      <DisplayRightsComposer
        authorizationId={request.authorizationId}
        creatorHandle={request.creatorName}
        initialTerms={request.initialTerms}
        secondaryAction={{
          label: t('common.cancel'),
          onClick: closeDisplayRightsCta,
        }}
        onDone={closeDisplayRightsCta}
      />
    </ModalShell>
  );
};
